import type { Loop } from './types';

/**
 * One selected thing.
 *
 * Two granularities, matching the vocabulary: a whole stitched chain, or the run
 * of segments one entity contributed to it. Outside mapping mode only `loop` refs
 * are produced, but the shape is the same either way so nothing downstream has to
 * care which mode made the selection.
 */
export type SelectionRef =
  | { readonly kind: 'loop'; readonly loopId: number }
  | {
      readonly kind: 'edge';
      readonly loopId: number;
      readonly chunkIndex: number;
    };

/**
 * Stable string key for a ref, so selections can live in a `Map` and keep
 * insertion order — which is what makes "the first thing you picked" meaningful.
 */
export const selectionKey = (ref: SelectionRef): string =>
  ref.kind === 'loop' ? `L${ref.loopId}` : `E${ref.loopId}:${ref.chunkIndex}`;

export type Selection = ReadonlyMap<string, SelectionRef>;

export const EMPTY_SELECTION: Selection = new Map();

/** Adds refs, ignoring ones already present. Returns the input if nothing changed. */
export const addToSelection = (
  selection: Selection,
  refs: readonly SelectionRef[],
): Selection => {
  const additions = refs.filter((ref) => !selection.has(selectionKey(ref)));
  if (additions.length === 0) {
    return selection;
  }
  const next = new Map(selection);
  additions.forEach((ref) => next.set(selectionKey(ref), ref));
  return next;
};

/** Removes refs. Returns the input if none were present. */
export const removeFromSelection = (
  selection: Selection,
  refs: readonly SelectionRef[],
): Selection => {
  const removals = refs.filter((ref) => selection.has(selectionKey(ref)));
  if (removals.length === 0) {
    return selection;
  }
  const next = new Map(selection);
  removals.forEach((ref) => next.delete(selectionKey(ref)));
  return next;
};

/** Adds refs that are absent and removes ones that are present. */
export const toggleInSelection = (
  selection: Selection,
  refs: readonly SelectionRef[],
): Selection => {
  if (refs.length === 0) {
    return selection;
  }
  const next = new Map(selection);
  refs.forEach((ref) => {
    const key = selectionKey(ref);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.set(key, ref);
    }
  });
  return next;
};

/** Builds a selection from refs, preserving order and dropping duplicates. */
export const selectionOf = (refs: readonly SelectionRef[]): Selection =>
  addToSelection(EMPTY_SELECTION, refs);

/**
 * Drops whatever a hidden layer took with it.
 *
 * Pruning rather than clearing, per the spec: hiding one layer leaves selections
 * on other layers alone. Because stitching runs across layers, a loop counts as
 * gone only when *every* chunk of it is hidden — a loop with one chunk on a hidden
 * layer is still visible, still pickable, and so stays selected. An edge ref goes
 * as soon as its own chunk is hidden.
 *
 * Nothing is remembered, so re-showing the layer does not bring the selection back.
 */
export const pruneHiddenFromSelection = (
  selection: Selection,
  loops: readonly Loop[],
  hiddenLayers: ReadonlySet<number>,
): Selection => {
  if (selection.size === 0) {
    return selection;
  }

  const byId = new Map(loops.map((loop) => [loop.id, loop]));
  const doomed: SelectionRef[] = [];

  selection.forEach((ref) => {
    const loop = byId.get(ref.loopId);
    if (!loop) {
      doomed.push(ref);
      return;
    }
    if (ref.kind === 'edge') {
      const chunk = loop.chunks[ref.chunkIndex];
      if (!chunk || hiddenLayers.has(chunk.layer)) {
        doomed.push(ref);
      }
      return;
    }
    const isEntirelyHidden = loop.chunks.every((chunk) =>
      hiddenLayers.has(chunk.layer),
    );
    if (isEntirelyHidden) {
      doomed.push(ref);
    }
  });

  return removeFromSelection(selection, doomed);
};

/** What the details overlay reports about a selection. */
export type SelectionSummary = {
  /** Number of selected items, at whatever granularity they were picked. */
  count: number;
  /** Total path length, in drawing units. */
  length: number;
  /**
   * Total enclosed area, or `null` when nothing closed contributes one. `null`
   * rather than `0` so the overlay can omit the row entirely.
   */
  area: number | null;
};

export const EMPTY_SUMMARY: SelectionSummary = {
  count: 0,
  length: 0,
  area: null,
};

/**
 * Measures a selection.
 *
 * Area comes only from whole closed loops: an edge chunk is an open run of
 * segments and encloses nothing, and a loop that never closed has no inside.
 */
export const summarizeSelection = (
  selection: Selection,
  loops: readonly Loop[],
): SelectionSummary => {
  const byId = new Map(loops.map((loop) => [loop.id, loop]));
  let count = 0;
  let length = 0;
  let area: number | null = null;

  selection.forEach((ref) => {
    const loop = byId.get(ref.loopId);
    if (!loop) {
      return;
    }
    count += 1;

    if (ref.kind === 'edge') {
      length += loop.chunks[ref.chunkIndex]?.length ?? 0;
      return;
    }

    length += loop.length;
    if (loop.isClosed && loop.area !== null) {
      area = (area ?? 0) + loop.area;
    }
  });

  return { count, length, area };
};

/** The edge a drill-down last opened, or `null` when nothing is drilled into. */
export type DrilledEdge = {
  readonly loopId: number;
  readonly chunkIndex: number;
} | null;

/**
 * Alternates between the edge under the pointer and the loop that owns it.
 *
 * Driven by an explicit memory of what was last drilled into rather than by
 * reading the current selection, because the single clicks that make up a
 * double-click land first and leave the loop selected — inferring from the
 * selection would therefore see "loop" every time and never come back out.
 *
 * Returns both the ref to select and the memory to carry forward, so the caller
 * holds no rule of its own.
 */
export const drillSelection = (
  hit: { loopId: number; chunkIndex: number },
  drilled: DrilledEdge,
): { ref: SelectionRef; drilled: DrilledEdge } => {
  const isSameEdge =
    drilled !== null &&
    drilled.loopId === hit.loopId &&
    drilled.chunkIndex === hit.chunkIndex;

  return isSameEdge
    ? { ref: { kind: 'loop', loopId: hit.loopId }, drilled: null }
    : {
        ref: {
          kind: 'edge',
          loopId: hit.loopId,
          chunkIndex: hit.chunkIndex,
        },
        drilled: { loopId: hit.loopId, chunkIndex: hit.chunkIndex },
      };
};

/** Every loop with at least one chunk drawn on `layerIndex`. */
export const loopsOnLayer = (
  loops: readonly Loop[],
  layerIndex: number,
): SelectionRef[] =>
  loops
    .filter((loop) => loop.layers.has(layerIndex))
    .map((loop) => ({ kind: 'loop', loopId: loop.id }));

/**
 * Flattens a selection into segment endpoint pairs for the accent overlay:
 * `[x0,y0,z0, x1,y1,z1, …]`, ready for a stroke batch.
 *
 * Hidden chunks are skipped, so painting can never reveal geometry the user turned
 * off — the same rule picking follows.
 */
export const selectionPositions = (
  selection: Selection,
  loops: readonly Loop[],
  hiddenLayers?: ReadonlySet<number>,
): number[] => {
  const byId = new Map(loops.map((loop) => [loop.id, loop]));
  const positions: number[] = [];

  const pushChunk = (points: readonly (readonly [number, number])[]) => {
    for (let index = 1; index < points.length; index += 1) {
      positions.push(
        points[index - 1][0],
        points[index - 1][1],
        0,
        points[index][0],
        points[index][1],
        0,
      );
    }
  };

  selection.forEach((ref) => {
    const loop = byId.get(ref.loopId);
    if (!loop) {
      return;
    }
    const chunks =
      ref.kind === 'edge'
        ? [loop.chunks[ref.chunkIndex]].filter(Boolean)
        : loop.chunks;

    chunks.forEach((chunk) => {
      if (hiddenLayers?.has(chunk.layer)) {
        return;
      }
      pushChunk(chunk.points);
    });
  });

  return positions;
};
